/**
 * 콘솔 상단에 미인증 사용자에게 노출되는 배너. 메일 재발송 버튼 + dismiss
 * (세션 동안 숨김).
 */
import { useState } from "react";
import { Mail, X } from "lucide-react";
import { resendVerification, extractErrorMessage } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth-store";

export function EmailVerificationBanner(): JSX.Element | null {
  const account = useAuthStore((s) => s.account);
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // emailVerified 가 명시적 false 일 때만 노출 (undefined = 구버전 cloud-api 호환).
  if (!account || account.emailVerified !== false) return null;
  if (dismissed) return null;

  const onResend = async (): Promise<void> => {
    setSending(true);
    setError(null);
    try {
      await resendVerification();
      setSent(true);
    } catch (err) {
      setError(extractErrorMessage(err, "메일 발송에 실패했습니다."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-b border-orange-500/30 bg-orange-500/5">
      <div className="container flex flex-wrap items-center justify-between gap-3 py-2.5 text-sm">
        <div className="flex items-center gap-2 text-foreground/90">
          <Mail className="h-4 w-4 shrink-0" aria-hidden />
          {sent ? (
            <span>인증 메일을 다시 보냈어요. 받은편지함을 확인해 주세요.</span>
          ) : (
            <span>
              이메일 인증이 아직 안 됐어요.{" "}
              <span className="text-muted-foreground">
                받은편지함의 인증 링크를 눌러 주세요.
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {error && (
            <span className="text-xs text-destructive">{error}</span>
          )}
          {!sent && (
            <button
              type="button"
              onClick={onResend}
              disabled={sending}
              className="text-xs font-medium underline-offset-4 hover:underline disabled:opacity-50"
            >
              {sending ? "보내는 중…" : "메일 재발송"}
            </button>
          )}
          <button
            type="button"
            aria-label="닫기"
            onClick={() => setDismissed(true)}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
